import React, { lazy, ReactNode, Suspense, useEffect } from 'react';
import { Navigate, RouteObject } from 'react-router-dom';
import routeConfig from 'app/config/routes';
import { Outlet } from 'react-router';
import SettingsModals from 'components/modals/SettingsModals';
import { ActivityIndicator } from '@kontentino/ui';
import { SettingsLayout } from 'app/modules/settings/components/SettingsLayout';
import { companyRouter } from 'app/modules/settings/modules/company';
import {
  globalProfilesRouter,
  profilesRouter,
} from 'app/modules/settings/modules/profiles';
import { useUser } from 'modules/user/userSelector';
import routes from 'constants/routes';
import { Module } from 'config';
import useSettingsV2Gate from 'app/hooks/useSettingsV2Gate';
import { useUserPermissions } from 'app/hooks/useUserPermissions';
import { UserPermission } from 'constants/userPermission';
import { SETTINGS_SECTION_PERMISSIONS } from 'app/modules/settings/components/settingsSidebarItems';

const AuthorizationRoute = lazy(
  () => import('app/modules/settings/modules/me/modules/authorization'),
);

const UsersRoute = lazy(() => import('app/modules/settings/modules/users'));

const BillingRoute = lazy(() => import('app/modules/settings/modules/billing'));

const BillingReturnHandler = lazy(
  () =>
    import(
      'app/modules/settings/modules/billing/components/BillingReturnHandler'
    ),
);

const ProfileGroupsLazy = lazy(
  () => import('app/modules/settings/modules/profileGroups'),
);

const SecurityRoute = lazy(
  () => import('app/modules/settings/modules/me/modules/security'),
);

const PersonalRoute = lazy(
  () => import('app/modules/settings/modules/me/modules/personal'),
);

const NotificationsRoute = lazy(
  () => import('app/modules/settings/modules/notifications'),
);

const DevicesRoute = lazy(() => import('app/modules/settings/modules/devices'));

const SETTINGS_ADMIN_ROLES = ['admin', 'manager'];

export const SettingsRoleGuard = ({ children }: { children: ReactNode }) => {
  const { role } = useUser();
  const { isSettingsV2Enabled } = useSettingsV2Gate();

  if (!isSettingsV2Enabled) return <>{children}</>;

  if (role === '') return null;

  if (!SETTINGS_ADMIN_ROLES.includes(role)) {
    return <Navigate to={routes.SETTINGS.PERSONAL} replace />;
  }

  return <>{children}</>;
};

export const SettingsPermissionGuard = ({
  permissions,
  children,
}: {
  permissions: UserPermission[];
  children: ReactNode;
}) => {
  const { role } = useUser();
  const { hasEvery } = useUserPermissions();
  const { isSettingsV2Enabled } = useSettingsV2Gate();

  if (!isSettingsV2Enabled) return <>{children}</>;

  if (role === '') return null;

  if (!hasEvery(permissions)) {
    return <Navigate to={routes.SETTINGS.PERSONAL} replace />;
  }

  return <>{children}</>;
};

const LegacyRedirect = ({ to }: { to: string }) => {
  useEffect(() => {
    window.location.replace(to);
  }, [to]);

  return null;
};

const SettingsV2Only = ({
  legacyUrl,
  isCompany = false,
  children,
}: {
  legacyUrl: string;
  isCompany?: boolean;
  children: ReactNode;
}) => {
  const { isSettingsV2Enabled, isCompanySettingsV2Enabled } =
    useSettingsV2Gate();
  const isEnabled = isCompany
    ? isCompanySettingsV2Enabled
    : isSettingsV2Enabled;

  if (!isEnabled) return <LegacyRedirect to={legacyUrl} />;

  return <>{children}</>;
};

const SettingsShell = () => {
  const { isSettingsV2Enabled } = useSettingsV2Gate();

  if (!isSettingsV2Enabled) {
    return (
      <Suspense
        fallback={
          <div className="tw-h-full tw-overflow-auto">
            <div className="tw-container tw-mx-auto tw-flex tw-max-w-[1800px] tw-justify-center tw-overflow-hidden tw-p-4">
              <ActivityIndicator />
            </div>
          </div>
        }
      >
        <div className="tw-h-full tw-overflow-hidden">
          <div className="tw-container tw-mx-auto tw-h-full tw-max-w-[1800px] tw-overflow-hidden">
            <Outlet />
          </div>
        </div>
        <SettingsModals />
      </Suspense>
    );
  }

  return (
    <>
      <SettingsLayout>
        <div className="tw-h-full tw-overflow-auto">
          <div className="tw-container tw-mx-auto tw-max-w-[1800px] tw-overflow-hidden">
            {/* Inner Suspense keeps SettingsLayout mounted while a lazy sub-route loads. */}
            <Suspense
              fallback={
                <div className="tw-flex tw-justify-center tw-p-4">
                  <ActivityIndicator />
                </div>
              }
            >
              <Outlet />
            </Suspense>
          </div>
        </div>
      </SettingsLayout>
      <SettingsModals />
    </>
  );
};

export const settingsRouter: RouteObject = {
  path: routeConfig.settings.path,
  element: <SettingsShell />,
  children: [
    {
      index: true,
      element: <Navigate to={routeConfig.settings.routes.authorization.path} />,
    },
    {
      path: routeConfig.settings.routes.authorization.path,
      element: (
        <SettingsRoleGuard>
          <AuthorizationRoute />
        </SettingsRoleGuard>
      ),
    },
    {
      path: routeConfig.settings.routes.users.path,
      element: (
        <SettingsPermissionGuard
          permissions={SETTINGS_SECTION_PERMISSIONS.USERS}
        >
          <UsersRoute />
        </SettingsPermissionGuard>
      ),
    },
    {
      ...profilesRouter,
      element: (
        <SettingsV2Only legacyUrl={Module.OldApp.URL.SETTINGS.PROFILES}>
          <SettingsPermissionGuard
            permissions={SETTINGS_SECTION_PERMISSIONS.PROFILES}
          >
            {profilesRouter.element}
          </SettingsPermissionGuard>
        </SettingsV2Only>
      ),
    },
    {
      ...globalProfilesRouter,
      element: (
        <SettingsV2Only legacyUrl={Module.OldApp.URL.SETTINGS.KONTENT_BASE}>
          <SettingsPermissionGuard
            permissions={SETTINGS_SECTION_PERMISSIONS.GLOBAL_PROFILES}
          >
            {globalProfilesRouter.element}
          </SettingsPermissionGuard>
        </SettingsV2Only>
      ),
    },
    {
      path: routeConfig.settings.routes.profileGroups.path,
      element: (
        <SettingsV2Only legacyUrl={Module.OldApp.URL.SETTINGS.PROFILE_GROUPS}>
          <SettingsPermissionGuard
            permissions={SETTINGS_SECTION_PERMISSIONS.PROFILE_GROUPS}
          >
            <ProfileGroupsLazy />
          </SettingsPermissionGuard>
        </SettingsV2Only>
      ),
    },
    {
      path: routeConfig.settings.routes.billing.path,
      element: <BillingRoute />,
    },
    {
      path: `${routeConfig.settings.routes.billing.path}/${routeConfig.settings.routes.billing.routes.return.path}`,
      element: <BillingReturnHandler />,
    },
    {
      path: routeConfig.settings.routes.security.path,
      element: <SecurityRoute />,
    },
    {
      path: routeConfig.settings.routes.personal.path,
      element: <PersonalRoute />,
    },
    {
      path: routeConfig.settings.routes.notifications.path,
      element: <NotificationsRoute />,
    },
    {
      path: routeConfig.settings.routes.devices.path,
      element: <DevicesRoute />,
    },
    {
      ...companyRouter,
      element: (
        <SettingsV2Only
          legacyUrl={Module.OldApp.URL.SETTINGS.COMPANY_SETTINGS}
          isCompany
        >
          <SettingsPermissionGuard
            permissions={SETTINGS_SECTION_PERMISSIONS.COMPANY_SETTINGS}
          >
            {companyRouter.element}
          </SettingsPermissionGuard>
        </SettingsV2Only>
      ),
    },
  ],
};
